// docs/35 WL10 — the creatures' calls. A songbird sings, a crow caws, a hawk screams overhead, frogs
// croak, bats squeak, mice skitter, gnats whine, and a bird that takes off beats its wings. Like the
// pose (./motion.ts), a call is a function of the row and the server time only: each species calls at
// most once a slot of `every` ms, at a seeded moment, on a seeded chance; a take-off is the start of a
// flying leg after a grounded one. So every client in the party hears the same call at the same
// moment, from the same creature. The layer (game/wildlife) plays them quietly, panned to where the
// creature is, and only where the party could see it.
import { GAIT, SPECIES, type Species, type WildRow } from './motion';

/** Each calling species: the bank, how often it may call, how likely in a slot, and how loud. */
export const CALLS: Partial<Record<Species, { bank: string; every: number; chance: number; gain: number }>> = {
  songbird: { bank: 'wild.songbird', every: 7000, chance: 0.5, gain: 0.55 },
  crow: { bank: 'wild.crow', every: 9500, chance: 0.45, gain: 0.6 },
  swallow: { bank: 'wild.swallow', every: 8000, chance: 0.35, gain: 0.45 },
  hawk: { bank: 'wild.hawk', every: 12000, chance: 0.5, gain: 0.6 },
  bat: { bank: 'wild.bat', every: 5000, chance: 0.4, gain: 0.45 },
  mouse: { bank: 'wild.mouse', every: 9000, chance: 0.35, gain: 0.4 },
  rat: { bank: 'wild.mouse', every: 8000, chance: 0.4, gain: 0.45 },
  gnats: { bank: 'wild.insect', every: 7000, chance: 0.45, gain: 0.3 },
  dragonfly: { bank: 'wild.insect', every: 9000, chance: 0.35, gain: 0.3 },
  scorpion: { bank: 'wild.critter', every: 11000, chance: 0.35, gain: 0.35 },
  lizard: { bank: 'wild.critter', every: 12000, chance: 0.3, gain: 0.3 },
  spider: { bank: 'wild.critter', every: 14000, chance: 0.25, gain: 0.3 },
  frog: { bank: 'wild.frog', every: 5500, chance: 0.55, gain: 0.55 },
};

/** Birds whose take-off is heard. */
const WINGED: ReadonlySet<Species> = new Set(['songbird', 'crow', 'swallow', 'hawk']);
const AIR: ReadonlySet<number> = new Set([GAIT.fly, GAIT.flutter, GAIT.glide]);
const GROUND: ReadonlySet<number> = new Set([GAIT.rest, GAIT.walk, GAIT.hop, GAIT.peck, GAIT.perch]);

export interface Call { row: number; bank: string; at: number; gain: number; seed: number }

function mix(a: number, b: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be59b, 0xc2b2ae35);
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d); h ^= h >>> 13;
  return h >>> 0;
}

/** The calls `row` makes in the window (t0, t1] of server time, in time order. */
export function callsIn(row: WildRow, t0: number, t1: number): Call[] {
  if (!(t1 > t0)) return [];
  const species = SPECIES[row.species];
  if (!species || row.leaving) return [];
  const out: Call[] = [];
  const c = CALLS[species];
  if (c) {
    for (let slot = Math.floor(t0 / c.every); slot * c.every <= t1; slot++) {
      const h = mix(row.seed, slot);
      if ((h % 1000) / 1000 >= c.chance) continue;
      const at = slot * c.every + (mix(h, 7) % c.every);
      if (at > t0 && at <= t1) out.push({ row: row.id, bank: c.bank, at, gain: c.gain, seed: h });
    }
  }
  if (WINGED.has(species)) {
    for (let i = 1; i < row.legs.length; i++) {
      const leg = row.legs[i]!, prev = row.legs[i - 1]!;
      if (leg.at > t0 && leg.at <= t1 && AIR.has(leg.gait) && GROUND.has(prev.gait)) {
        out.push({ row: row.id, bank: 'wild.wings', at: leg.at, gain: species === 'hawk' ? 0.35 : 0.5, seed: mix(row.seed, leg.at) });
      }
    }
  }
  return out.sort((a, b) => a.at - b.at);
}
